import { mutationOptions, type QueryClient } from '@tanstack/react-query';

import type { AddCreatorRequestDTO, RemoveCreatorRequestDTO } from './dto';
import {
  AddCreatorsToSavedList,
  CreateSavedList,
  DeleteSavedList,
  RemoveCreatorsFromSavedList,
  UpdateSavedListTitle,
} from './api';
import { savedCreatorsListQueryKeys } from './query-keys';

export type UpdateSavedListTitleMutationRequest = {
  listId: string;
  title: string;
};

export type AddCreatorsToSavedListMutationRequest = {
  listId: string;
  request: AddCreatorRequestDTO;
};

export type RemoveCreatorsFromSavedListMutationRequest = {
  listId: string;
  request: RemoveCreatorRequestDTO;
};

function invalidateSavedList(queryClient: QueryClient, workspaceId: string, listId: string) {
  void Promise.all([
    queryClient.invalidateQueries({
      queryKey: savedCreatorsListQueryKeys.lists(workspaceId),
    }),
    queryClient.invalidateQueries({
      queryKey: savedCreatorsListQueryKeys.list(workspaceId, listId),
    }),
    queryClient.invalidateQueries({
      queryKey: savedCreatorsListQueryKeys.creators(workspaceId, listId),
    }),
  ]);
}

export function createSavedListMutationOptions(queryClient: QueryClient, workspaceId: string) {
  return mutationOptions({
    mutationFn: (title: string) =>
      CreateSavedList({
        title,
        workspace_id: workspaceId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: savedCreatorsListQueryKeys.lists(workspaceId),
      });
    },
  });
}

export function updateSavedListTitleMutationOptions(queryClient: QueryClient, workspaceId: string) {
  return mutationOptions({
    mutationFn: ({ listId, title }: UpdateSavedListTitleMutationRequest) =>
      UpdateSavedListTitle(listId, title),
    onSuccess: (_response, { listId }) => {
      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: savedCreatorsListQueryKeys.lists(workspaceId),
        }),
        queryClient.invalidateQueries({
          queryKey: savedCreatorsListQueryKeys.list(workspaceId, listId),
        }),
      ]);
    },
  });
}

export function deleteSavedListMutationOptions(queryClient: QueryClient, workspaceId: string) {
  return mutationOptions({
    mutationFn: (listId: string) => DeleteSavedList(listId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: savedCreatorsListQueryKeys.lists(workspaceId),
      });
    },
  });
}

export function addCreatorsToSavedListMutationOptions(
  queryClient: QueryClient,
  workspaceId: string,
) {
  return mutationOptions({
    mutationFn: ({ listId, request }: AddCreatorsToSavedListMutationRequest) =>
      AddCreatorsToSavedList(listId, request),
    onSuccess: (_response, { listId }) => {
      invalidateSavedList(queryClient, workspaceId, listId);
    },
  });
}

export function removeCreatorsFromSavedListMutationOptions(
  queryClient: QueryClient,
  workspaceId: string,
) {
  return mutationOptions({
    mutationFn: ({ listId, request }: RemoveCreatorsFromSavedListMutationRequest) =>
      RemoveCreatorsFromSavedList(listId, request),
    onSuccess: (_response, { listId }) => {
      invalidateSavedList(queryClient, workspaceId, listId);
    },
  });
}
